import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  Grid,
  Chip,
  Divider,
  TextField,
  Button,
  Avatar,
  Paper,
  Stack,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  Switch,
  Tabs,
  Tab,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  Alert
} from '@mui/material';
import {
  FileText,
  Clock,
  User,
  Users,
  UserPlus,
  UserMinus,
  Lock,
  Globe,
  CheckCircle2,
  AlertCircle,
  Bell,
  Settings as SettingsIcon,
  MoreVertical,
  Copy,
  Layers,
  GalleryHorizontalEnd,
  FileDown,
  Loader2,
  MessageSquare,
  FileCheck,
  RefreshCw,
  ToggleLeft,
  ChevronDown,
  ChevronRight,
  Building2,
  Link as LinkIcon,
  Search,
  Trash2,
  Pencil,
  Check,
  X
} from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../context/SocketContext';
import QuoteBuilder from '../../components/QuoteBuilder';
import QuoteSummary from '../../components/QuoteSummary';
import RecurringCostsEditor from '../../components/RecurringCostsEditor';
import ProductsEditor from '../../components/ProductsEditor';
import TemplateSelector from '../../components/TemplateSelector';
import PreviewGallery from '../../components/PreviewGallery';

const steps = ['SUBMITTED', 'ANALYSIS', 'QUOTED', 'ACCEPTED', 'DEVELOPMENT', 'COMPLETED'];
const statusLabels = {
  SUBMITTED: 'Soumis',
  ANALYSIS: 'Analyse',
  QUOTED: 'Devis',
  ACCEPTED: 'Validé',
  REJECTED: 'Refusé',
  DEVELOPMENT: 'Dev',
  COMPLETED: 'Terminé'
};

function SubmissionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { socket, joinSubmission, leaveSubmission } = useSocket();
  const [submission, setSubmission] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [elements, setElements] = useState([]);
  const [notificationUsers, setNotificationUsers] = useState([]);
  const [notifCreatedById, setNotifCreatedById] = useState(null);
  const [expandedUsers, setExpandedUsers] = useState({});
  const [notifDialogOpen, setNotifDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [templateSelectorOpen, setTemplateSelectorOpen] = useState(false);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [convertTemplateName, setConvertTemplateName] = useState('');
  const [actionsMenuAnchor, setActionsMenuAnchor] = useState(null);
  const [quoteKey, setQuoteKey] = useState(0); // For forcing refresh
  const [previewGalleryOpen, setPreviewGalleryOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  // Client notification preferences
  const [myNotifPrefs, setMyNotifPrefs] = useState(null);
  const [clientNotifDialogOpen, setClientNotifDialogOpen] = useState(false);
  // Attribution system
  const [teamDialogOpen, setTeamDialogOpen] = useState(false);
  const [teamDialogTab, setTeamDialogTab] = useState(0); // 0 = Attribution, 1 = Notifications
  const [assignments, setAssignments] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  // Assign client dialog
  const [assignClientDialogOpen, setAssignClientDialogOpen] = useState(false);
  const [clientCompanies, setClientCompanies] = useState([]);
  const [clientCompanyUsers, setClientCompanyUsers] = useState([]);
  const [assignClientForm, setAssignClientForm] = useState({ companyId: '', clientId: '' });
  const [assignClientLoading, setAssignClientLoading] = useState(false);
  // Inline editing
  const [editingField, setEditingField] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [quoteTotals, setQuoteTotals] = useState(null);

  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPERADMIN';
  const isDev = user?.role === 'DEV' || user?.role === 'TECHNICIEN';
  const isInternalRole = isAdmin || isDev; // Rôles internes
  const isClient = user?.role === 'CLIENT' || user?.role === 'CLIENTADMIN';

  const fetchDetail = async () => {
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch(`/api/submissions/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) setSubmission(data);

      const elResponse = await fetch(`/api/submissions/${id}/elements`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const elData = await elResponse.json();
      if (elResponse.ok) setElements(elData);

      // Fetch notification preferences (for internal roles)
      if (isInternalRole) {
        const notifsResponse = await fetch(`/api/submissions/${id}/notifications`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const notifsData = await notifsResponse.json();
        if (notifsResponse.ok) {
          setNotificationUsers(notifsData.users || []);
          setNotifCreatedById(notifsData.createdById || null);
        }
      }

      // Fetch my notification preferences (for clients)
      const myPrefsResponse = await fetch(`/api/submissions/${id}/notifications/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (myPrefsResponse.ok) {
        const prefsData = await myPrefsResponse.json();
        setMyNotifPrefs(prefsData);
      }

      // Fetch quote summary for auto-budget
      try {
        const summaryResponse = await fetch(`/api/submissions/${id}/quote/summary`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (summaryResponse.ok) {
          const summaryData = await summaryResponse.json();
          setQuoteTotals(summaryData.totals || null);
        }
      } catch (e) { /* quote may not exist yet */ }
    } catch (err) {
      console.error('Fetch error');
    }
    setLoading(false);
  };

  const fetchAssignments = async () => {
    if (!isInternalRole) return;
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch(`/api/submissions/${id}/assignments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAssignments(data.assignments || []);
        setAvailableUsers(data.availableUsers || []);
      }
    } catch (err) {
      console.error('Fetch assignments error');
    }
  };

  const handleAssignUser = async (userId) => {
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch(`/api/submissions/${id}/assignments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ userId })
      });
      if (response.ok) {
        fetchAssignments();
        // Also refresh notifications since they're linked
        fetchDetail();
      }
    } catch (err) {
      console.error('Assign user error');
    }
  };

  const handleSelfAssign = async () => {
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch(`/api/submissions/${id}/assignments/self`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchAssignments();
        fetchDetail();
      }
    } catch (err) {
      console.error('Self assign error');
    }
  };

  const handleUnassignUser = async (userId) => {
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch(`/api/submissions/${id}/assignments/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchAssignments();
        fetchDetail();
      }
    } catch (err) {
      console.error('Unassign user error');
    }
  };

  // Fetch client companies for assign dialog
  const fetchClientCompanies = async () => {
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch('/api/companies/clients', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setClientCompanies(data);
      }
    } catch (err) {
      console.error('Fetch companies error');
    }
  };

  // Fetch users of selected company
  const fetchCompanyUsers = async (companyId) => {
    if (!companyId) {
      setClientCompanyUsers([]);
      return;
    }
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch(`/api/companies/${companyId}/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setClientCompanyUsers(data);
        // Auto-select first user or CLIENTADMIN
        if (data.length === 1) {
          setAssignClientForm(prev => ({ ...prev, clientId: data[0].id }));
        } else if (data.length > 0) {
          const clientAdmin = data.find(u => u.role === 'CLIENTADMIN');
          if (clientAdmin) {
            setAssignClientForm(prev => ({ ...prev, clientId: clientAdmin.id }));
          }
        }
      }
    } catch (err) {
      console.error('Fetch company users error');
    }
  };

  // Handle assign client
  const handleAssignClient = async () => {
    if (!assignClientForm.companyId) return;
    
    setAssignClientLoading(true);
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch(`/api/submissions/${id}/assign-client`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          companyId: assignClientForm.companyId,
          clientId: assignClientForm.clientId || null
        })
      });
      
      if (response.ok) {
        setAssignClientDialogOpen(false);
        setAssignClientForm({ companyId: '', clientId: '' });
        fetchDetail();
      } else {
        const errorData = await response.json();
        alert(`Erreur: ${errorData.error || 'Impossible d\'attribuer le devis'}`);
      }
    } catch (err) {
      console.error('Assign client error:', err);
      alert('Erreur lors de l\'attribution du devis');
    }
    setAssignClientLoading(false);
  };

  // Open assign client dialog
  const openAssignClientDialog = () => {
    fetchClientCompanies();
    setAssignClientForm({ companyId: '', clientId: '' });
    setClientCompanyUsers([]);
    setAssignClientDialogOpen(true);
  };

  useEffect(() => {
    fetchDetail();
  }, [id]);

  useEffect(() => {
    if (isInternalRole && id) {
      fetchAssignments();
    }
  }, [id, isInternalRole]);

  // Socket: join room and listen for changes
  useEffect(() => {
    if (!socket || !id) return;

    joinSubmission(id);

    const handleStatusChanged = (data) => {
      if (data.submissionId === id) {
        fetchDetail();
      }
    };

    const handleAssignmentChanged = (data) => {
      if (data.submissionId === id) {
        fetchAssignments();
      }
    };

    socket.on('statusChanged', handleStatusChanged);
    socket.on('assignmentChanged', handleAssignmentChanged);

    return () => {
      leaveSubmission(id);
      socket.off('statusChanged', handleStatusChanged);
      socket.off('assignmentChanged', handleAssignmentChanged);
    };
  }, [socket, id, joinSubmission, leaveSubmission]);

  const handleUpdateStatus = async (newStatus) => {
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch(`/api/submissions/${id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus }),
      });
      if (response.ok) {
        await fetchDetail();
        // Socket émettra automatiquement l'événement depuis le backend
      }
    } catch (err) {
      console.error('Status error');
    }
  };

  const handleAcceptQuote = async () => {
    if (!window.confirm('Voulez-vous accepter ce devis et valider le projet ?')) return;
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch(`/api/submissions/${id}/accept-quote`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        await fetchDetail();
        // Socket émettra automatiquement l'événement depuis le backend
      }
    } catch (err) {
      console.error('Accept error');
    }
  };

  // Toggle user subscription (on/off)
  const handleToggleUserSubscription = async (userId, isSubscribed) => {
    const token = localStorage.getItem('portalToken');
    try {
      if (isSubscribed) {
        // Remove user from notifications
        await fetch(`/api/submissions/${id}/notifications/${userId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } else {
        // Add user to notifications with all prefs enabled
        await fetch(`/api/submissions/${id}/notifications`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ userId })
        });
      }
      // Refresh local state
      const notifsResponse = await fetch(`/api/submissions/${id}/notifications`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (notifsResponse.ok) {
        const notifsData = await notifsResponse.json();
        setNotificationUsers(notifsData.users || []);
        setNotifCreatedById(notifsData.createdById || null);
      }
    } catch (err) {
      console.error('Toggle user subscription error:', err);
    }
  };

  // Update individual notification preference
  const handleUpdateNotifPref = async (userId, prefKey, value) => {
    const token = localStorage.getItem('portalToken');
    try {
      await fetch(`/api/submissions/${id}/notifications/${userId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ [prefKey]: value })
      });
      // Update local state
      setNotificationUsers(prev => prev.map(u => {
        if (u.id === userId && u.prefs) {
          return {
            ...u,
            prefs: { ...u.prefs, [prefKey]: value }
          };
        }
        return u;
      }));
    } catch (err) {
      console.error('Update notification pref error:', err);
    }
  };

  // Toggle all prefs for a user
  const handleToggleAllPrefs = async (userId, value) => {
    const token = localStorage.getItem('portalToken');
    try {
      await fetch(`/api/submissions/${id}/notifications/${userId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          notifyMessages: value,
          notifyDocuments: value,
          notifyStatus: value,
          notifyOptions: value
        })
      });
      // Update local state
      setNotificationUsers(prev => prev.map(u => {
        if (u.id === userId && u.prefs) {
          return {
            ...u,
            prefs: {
              ...u.prefs,
              notifyMessages: value,
              notifyDocuments: value,
              notifyStatus: value,
              notifyOptions: value
            }
          };
        }
        return u;
      }));
    } catch (err) {
      console.error('Toggle all prefs error:', err);
    }
  };

  // Toggle user expansion in the dialog
  const toggleUserExpand = (userId) => {
    setExpandedUsers(prev => ({ ...prev, [userId]: !prev[userId] }));
  };

  // Update client's own notification preferences
  const handleUpdateMyNotifPref = async (key, value) => {
    const token = localStorage.getItem('portalToken');
    try {
      const response = await fetch(`/api/submissions/${id}/notifications/me`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ [key]: value })
      });
      if (response.ok) {
        const data = await response.json();
        setMyNotifPrefs(data);
      }
    } catch (err) {
      console.error('Update my notif pref error:', err);
    }
  };

  // Toggle client subscription on/off
  const handleToggleMySubscription = async () => {
    const token = localStorage.getItem('portalToken');
    try {
      const response = await fetch(`/api/submissions/${id}/notifications/me`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          isSubscribed: !myNotifPrefs?.isSubscribed,
          notifyMessages: true,
          notifyDocuments: true,
          notifyStatus: true,
          notifyOptions: true
        })
      });
      if (response.ok) {
        const data = await response.json();
        setMyNotifPrefs(data);
      }
    } catch (err) {
      console.error('Toggle my subscription error:', err);
    }
  };

  const handleConvertToTemplate = async () => {
    if (!convertTemplateName.trim()) return;
    
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch(`/api/templates/convert-from-submission/${id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ name: convertTemplateName })
      });
      
      if (response.ok) {
        setConvertDialogOpen(false);
        setConvertTemplateName('');
        alert('Template créé avec succès !');
      }
    } catch (err) {
      console.error('Convert to template error:', err);
    }
  };

  const handleTemplateApplied = () => {
    setQuoteKey(prev => prev + 1); // Force refresh of QuoteBuilder
    fetchDetail();
  };

  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch(`/api/submissions/${id}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Devis_${submission.title.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        console.error('PDF download failed');
      }
    } catch (err) {
      console.error('PDF download error:', err);
    }
    setPdfLoading(false);
  };

  const startEditing = (field) => {
    setEditingField(field);
    setEditValues({
      title: submission.title,
      description: submission.description || '',
      budget: submission.budget || '',
      createdAt: submission.createdAt ? new Date(submission.createdAt).toISOString().split('T')[0] : ''
    });
  };

  const cancelEditing = () => {
    setEditingField(null);
    setEditValues({});
  };

  const saveField = async (field) => {
    try {
      const token = localStorage.getItem('portalToken');
      const body = {};
      if (field === 'createdAt') {
        body.createdAt = editValues.createdAt;
      } else {
        body[field] = editValues[field];
      }
      const response = await fetch(`/api/submissions/${id}/details`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });
      if (response.ok) {
        const updated = await response.json();
        setSubmission(prev => ({ ...prev, ...updated }));
        setEditingField(null);
      }
    } catch (err) {
      console.error('Save error:', err);
    }
  };

  const handleDeleteSubmission = async () => {
    setDeleteLoading(true);
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch(`/api/submissions/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        setDeleteDialogOpen(false);
        navigate(isAdmin ? '/admin/submissions' : '/portal/submissions');
      } else {
        const data = await response.json();
        alert(data.error || 'Erreur lors de la suppression');
      }
    } catch (err) {
      console.error('Delete submission error:', err);
      alert('Erreur lors de la suppression du devis');
    }
    setDeleteLoading(false);
  };

  if (loading || !submission) return <Typography sx={{ p: 3 }}>Chargement...</Typography>;

  const currentStep = steps.indexOf(submission.status);
  const isQuoted = submission.status === 'QUOTED';

  return (
    <Box>
      {/* Workflow Stepper - En haut, cliquable pour les internes */}
      <Card sx={{ p: 2, mb: 3, bgcolor: 'background.paper', backgroundImage: 'none' }}>
        <Stepper activeStep={currentStep} alternativeLabel>
          {steps.map((label, index) => {
            const isClickable = isInternalRole;
            const isActive = submission?.status === label;
            return (
              <Step 
                key={label}
                onClick={isClickable ? () => handleUpdateStatus(label) : undefined}
                sx={{ 
                  cursor: isClickable ? 'pointer' : 'default',
                  '&:hover': isClickable ? {
                    '& .MuiStepLabel-label': { color: 'primary.main' },
                    '& .MuiStepIcon-root': { transform: 'scale(1.1)' }
                  } : {}
                }}
              >
                <StepLabel 
                  StepIconProps={{ 
                    sx: { 
                      '&.Mui-active': { color: 'primary.main' }, 
                      '&.Mui-completed': { color: 'primary.main' },
                      transition: 'transform 0.2s'
                    } 
                  }}
                >
                  <Typography sx={{ 
                    fontSize: '0.7rem',
                    fontWeight: isActive ? 700 : 400,
                    transition: 'color 0.2s'
                  }}>
                    {statusLabels[label]}
                  </Typography>
                </StepLabel>
              </Step>
            );
          })}
        </Stepper>
      </Card>

      {/* Banner pour devis non attribué */}
      {isAdmin && submission.isUnassigned && (
        <Paper sx={{ 
          p: 2, mb: 3, 
          bgcolor: 'rgba(59, 130, 246, 0.1)', 
          border: '1px solid rgba(59, 130, 246, 0.3)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderRadius: 2
        }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Building2 color="#3b82f6" size={24} />
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#3b82f6' }}>
                Devis non attribué
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Ce devis n'est pas encore associé à un client. Attribuez-le pour que le client puisse y accéder.
              </Typography>
            </Box>
          </Stack>
          <Button 
            variant="contained" 
            startIcon={<LinkIcon size={18} />} 
            onClick={openAssignClientDialog}
            sx={{ fontWeight: 'bold', bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }}
          >
            Attribuer un client
          </Button>
        </Paper>
      )}

      {/* Banner Lock pour Devis */}
      {isQuoted && (
        <Paper sx={{ 
          p: 2, mb: 3, 
          bgcolor: 'rgba(245, 158, 11, 0.1)', 
          border: '1px solid rgba(245, 158, 11, 0.3)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderRadius: 2
        }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <AlertCircle color="#f59e0b" size={24} />
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#f59e0b' }}>
                {isAdmin ? 'Devis émis - En attente d\'acceptation' : 'Devis reçu - Action requise'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isAdmin ? 'Le client doit accepter le devis pour débloquer la suite.' : 'Veuillez réviser les documents et accepter le devis pour lancer le projet.'}
              </Typography>
            </Box>
          </Stack>
          {['CLIENT', 'CLIENTADMIN'].includes(user.role) && (
            <Button variant="contained" color="success" startIcon={<CheckCircle2 size={18} />} onClick={handleAcceptQuote} sx={{ fontWeight: 'bold' }}>
              Accepter le devis
            </Button>
          )}
        </Paper>
      )}

      {/* Header Info */}
      <Box sx={{ mb: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 4 }}>
        <Box sx={{ flexGrow: 1 }}>
          {/* Titre */}
          {editingField === 'title' ? (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <TextField
                value={editValues.title}
                onChange={(e) => setEditValues(prev => ({ ...prev, title: e.target.value }))}
                variant="standard"
                autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter') saveField('title'); if (e.key === 'Escape') cancelEditing(); }}
                InputProps={{ sx: { fontSize: '2rem', fontWeight: 800, letterSpacing: '-0.02em' } }}
                fullWidth
              />
              <IconButton size="small" onClick={() => saveField('title')} sx={{ color: '#00d4aa' }}><Check size={18} /></IconButton>
              <IconButton size="small" onClick={cancelEditing} sx={{ color: 'text.secondary' }}><X size={18} /></IconButton>
            </Stack>
          ) : (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="h3" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
                {submission.title}
              </Typography>
              {isInternalRole && (
                <IconButton size="small" onClick={() => startEditing('title')} sx={{ color: 'text.secondary', opacity: 0.4, '&:hover': { opacity: 1 } }}>
                  <Pencil size={16} />
                </IconButton>
              )}
            </Stack>
          )}

          {/* Numéro de référence */}
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontFamily: 'monospace',
              mb: 2,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: 'rgba(255,255,255,0.03)',
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              border: '1px solid rgba(255,255,255,0.06)'
            }}
          >
            <FileText size={14} />
            Réf: {submission.id.substring(0, 8).toUpperCase()}
          </Typography>

          <Stack direction="row" spacing={6} alignItems="flex-start">
            {/* Status & Date Group */}
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '0.1em', display: 'block', mb: 1 }}>
                STATUT & DATE
              </Typography>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Chip
                  label={statusLabels[submission.status]}
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'background.paper',
                    fontWeight: 'bold',
                    borderRadius: '4px',
                    height: '24px'
                  }}
                />
                {editingField === 'createdAt' ? (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <TextField
                      type="date"
                      value={editValues.createdAt}
                      onChange={(e) => setEditValues(prev => ({ ...prev, createdAt: e.target.value }))}
                      variant="standard"
                      size="small"
                      autoFocus
                      onKeyDown={(e) => { if (e.key === 'Enter') saveField('createdAt'); if (e.key === 'Escape') cancelEditing(); }}
                      InputProps={{ sx: { fontSize: '0.875rem' } }}
                    />
                    <IconButton size="small" onClick={() => saveField('createdAt')} sx={{ color: '#00d4aa' }}><Check size={14} /></IconButton>
                    <IconButton size="small" onClick={cancelEditing} sx={{ color: 'text.secondary' }}><X size={14} /></IconButton>
                  </Stack>
                ) : (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Clock size={14} />
                      {new Date(submission.createdAt).toLocaleDateString()}
                    </Typography>
                    {isInternalRole && (
                      <IconButton size="small" onClick={() => startEditing('createdAt')} sx={{ color: 'text.secondary', opacity: 0.4, '&:hover': { opacity: 1 }, p: 0.3 }}>
                        <Pencil size={12} />
                      </IconButton>
                    )}
                  </Stack>
                )}
              </Stack>
            </Box>

            {/* Description Group */}
            <Box sx={{ maxWidth: '400px' }}>
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '0.1em' }}>
                  DESCRIPTION
                </Typography>
                {isInternalRole && editingField !== 'description' && (
                  <IconButton size="small" onClick={() => startEditing('description')} sx={{ color: 'text.secondary', opacity: 0.4, '&:hover': { opacity: 1 }, p: 0.3 }}>
                    <Pencil size={12} />
                  </IconButton>
                )}
              </Stack>
              {editingField === 'description' ? (
                <Stack spacing={1}>
                  <TextField
                    value={editValues.description}
                    onChange={(e) => setEditValues(prev => ({ ...prev, description: e.target.value }))}
                    multiline
                    rows={3}
                    size="small"
                    autoFocus
                    fullWidth
                    variant="outlined"
                    sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem' } }}
                  />
                  <Stack direction="row" spacing={0.5}>
                    <IconButton size="small" onClick={() => saveField('description')} sx={{ color: '#00d4aa' }}><Check size={16} /></IconButton>
                    <IconButton size="small" onClick={cancelEditing} sx={{ color: 'text.secondary' }}><X size={16} /></IconButton>
                  </Stack>
                </Stack>
              ) : (
                <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.6, opacity: 0.8 }}>
                  {submission.description || 'Aucune description'}
                </Typography>
              )}
            </Box>

            {/* Financial Group */}
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '0.1em', display: 'block', mb: 1 }}>
                BUDGET PRÉVISIONNEL
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#00d4aa' }}>
                {(() => {
                  const computedBudget = quoteTotals
                    ? Math.ceil(((quoteTotals.developmentPrice || 0) + (quoteTotals.productsPrice || 0)) / 1000) * 1000
                    : null;
                  if (computedBudget && computedBudget > 0) {
                    return `$${computedBudget.toLocaleString()}`;
                  }
                  if (submission.budget) {
                    return typeof submission.budget === 'string' && submission.budget.startsWith('$')
                      ? submission.budget
                      : `$${typeof submission.budget === 'number' ? submission.budget.toLocaleString() : submission.budget}`;
                  }
                  return 'N/A';
                })()}
              </Typography>
            </Box>

            {isInternalRole && (
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '0.1em', display: 'block', mb: 1 }}>
                  ENTREPRISE
                </Typography>
                {submission.isUnassigned ? (
                  <Chip 
                    icon={<Clock size={14} />}
                    label="Non attribué" 
                    size="small"
                    sx={{ 
                      bgcolor: 'rgba(59, 130, 246, 0.2)', 
                      color: '#3b82f6', 
                      borderColor: '#3b82f6',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'rgba(59, 130, 246, 0.3)' }
                    }}
                    variant="outlined"
                    onClick={openAssignClientDialog}
                  />
                ) : (
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {submission.companyName || submission.company?.name || 'N/A'}
                  </Typography>
                )}
              </Box>
            )}
          </Stack>
        </Box>

        {/* Team & Notifications Button - Fixé à droite pour les internes */}
        {isInternalRole && (
          <Box sx={{ pt: 1, minWidth: 'fit-content' }}>
            <Tooltip title="Équipe & Notifications">
              <IconButton 
                onClick={() => { setTeamDialogOpen(true); fetchAssignments(); }} 
                size="small"
                sx={{ 
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '6px',
                  p: 0.5
                }}
              >
                <Users size={18} />
                {assignments.length > 0 && (
                  <Box sx={{
                    position: 'absolute',
                    top: -4,
                    right: -4,
                    bgcolor: '#00d4aa',
                    color: '#0a0a0f',
                    borderRadius: '50%',
                    width: 16,
                    height: 16,
                    fontSize: 10,
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {assignments.length}
                  </Box>
                )}
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {/* Client Notifications Button - Fixé à droite pour les clients */}
        {isClient && !isInternalRole && (
          <Box sx={{ pt: 1, minWidth: 'fit-content' }}>
            <Tooltip title="Mes notifications">
              <IconButton 
                onClick={() => setClientNotifDialogOpen(true)} 
                size="small"
                sx={{ 
                  border: `1px solid ${myNotifPrefs?.isSubscribed ? 'rgba(0,212,170,0.5)' : 'rgba(255,255,255,0.1)'}`,
                  borderRadius: '6px',
                  p: 0.5,
                  bgcolor: myNotifPrefs?.isSubscribed ? 'rgba(0,212,170,0.1)' : 'transparent'
                }}
              >
                <Bell size={18} color={myNotifPrefs?.isSubscribed ? '#00d4aa' : undefined} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={9}>
          <Stack spacing={3}>
            {/* Quote Builder Header with Actions */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                <FileText size={24} color="#00d4aa" />
                Devis détaillé
              </Typography>
              <Stack direction="row" spacing={1}>
                {/* Preview Button - visible pour tous */}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<GalleryHorizontalEnd size={16} />}
                  onClick={() => setPreviewGalleryOpen(true)}
                  sx={{ 
                    borderColor: 'rgba(139, 92, 246, 0.5)',
                    color: '#8b5cf6',
                    '&:hover': { borderColor: '#8b5cf6', bgcolor: 'rgba(139, 92, 246, 0.05)' }
                  }}
                >
                  Preview
                </Button>

                {/* PDF Export Button - visible pour tous */}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={pdfLoading ? <Loader2 size={16} className="animate-spin" /> : <FileDown size={16} />}
                  onClick={handleDownloadPdf}
                  disabled={pdfLoading}
                  sx={{ 
                    borderColor: 'rgba(239, 68, 68, 0.5)',
                    color: '#ef4444',
                    '&:hover': { borderColor: '#ef4444', bgcolor: 'rgba(239, 68, 68, 0.05)' }
                  }}
                >
                  {pdfLoading ? 'Génération...' : 'Exporter PDF'}
                </Button>
                
                {isAdmin && (
                  <>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Layers size={16} />}
                      onClick={() => setTemplateSelectorOpen(true)}
                    >
                      Appliquer un template
                    </Button>
                    <IconButton
                      size="small"
                      onClick={(e) => setActionsMenuAnchor(e.currentTarget)}
                    >
                      <MoreVertical size={18} />
                    </IconButton>
                    <Menu
                      anchorEl={actionsMenuAnchor}
                      open={Boolean(actionsMenuAnchor)}
                      onClose={() => setActionsMenuAnchor(null)}
                    >
                      <MenuItem onClick={() => { setActionsMenuAnchor(null); setConvertTemplateName(submission.title); setConvertDialogOpen(true); }}>
                        <ListItemIcon><Copy size={16} /></ListItemIcon>
                        <ListItemText>Convertir en template</ListItemText>
                      </MenuItem>
                      <Divider />
                      <MenuItem 
                        onClick={() => { setActionsMenuAnchor(null); setDeleteDialogOpen(true); }}
                        sx={{ color: 'error.main' }}
                      >
                        <ListItemIcon><Trash2 size={16} color="#f44336" /></ListItemIcon>
                        <ListItemText>Supprimer le devis</ListItemText>
                      </MenuItem>
                    </Menu>
                  </>
                )}
              </Stack>
            </Box>

            {/* Quote Builder */}
            <QuoteBuilder 
              key={quoteKey}
              submissionId={id} 
              isAdmin={isAdmin}
              isInternalUser={isInternalRole}
              onRefresh={fetchDetail} 
            />

            {/* Recurring Costs */}
            <Card sx={{ p: 3, bgcolor: 'background.paper', backgroundImage: 'none', border: '1px solid rgba(255, 255, 255, 0.06)' }}>
              <RecurringCostsEditor 
                key={quoteKey}
                submissionId={id} 
                isAdmin={isAdmin} 
                onRefresh={fetchDetail} 
              />
            </Card>

            {/* Products / Material */}
            <Card sx={{ p: 3, bgcolor: 'background.paper', backgroundImage: 'none', border: '1px solid rgba(255, 255, 255, 0.06)' }}>
              <ProductsEditor 
                key={`products-${quoteKey}`}
                submissionId={id}
                projectTypeId={submission?.projectType?.id}
                isAdmin={isAdmin} 
                onRefresh={fetchDetail} 
              />
            </Card>
          </Stack>
        </Grid>

        <Grid item xs={12} md={3}>
          <Stack spacing={2} sx={{ position: 'sticky', top: '20px' }}>
            {/* Quote Summary */}
            <QuoteSummary key={quoteKey} submissionId={id} />
          </Stack>
        </Grid>
      </Grid>

      {/* Team & Notifications Dialog with Tabs */}
      <Dialog 
        open={teamDialogOpen} 
        onClose={() => setTeamDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{ sx: { bgcolor: 'background.paper', backgroundImage: 'none' } }}
      >
        <Box sx={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
            <Users size={20} color="#00d4aa" />
            Équipe & Notifications
          </DialogTitle>
          <Tabs 
            value={teamDialogTab} 
            onChange={(e, v) => setTeamDialogTab(v)}
            sx={{ 
              px: 2,
              '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontWeight: 500, minWidth: 'auto', px: 2 },
              '& .Mui-selected': { color: '#00d4aa' },
              '& .MuiTabs-indicator': { backgroundColor: '#00d4aa' }
            }}
          >
            <Tab label="Attribution" icon={<UserPlus size={16} />} iconPosition="start" />
            <Tab label="Notifications" icon={<Bell size={16} />} iconPosition="start" />
          </Tabs>
        </Box>
        <DialogContent sx={{ p: 0 }}>
          {/* Tab 0: Attribution */}
          {teamDialogTab === 0 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ p: 2, pb: 1 }}>
                Assignez des membres de l'équipe à ce projet. Seuls les utilisateurs assignés peuvent être affectés aux sections et tâches.
              </Typography>

              {/* Self-assign button if not already assigned */}
              {!assignments.find(a => a.userId === user?.id) && (
                <Box sx={{ px: 2, pb: 1 }}>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    startIcon={<UserPlus size={16} />}
                    onClick={handleSelfAssign}
                    sx={{ borderColor: '#00d4aa', color: '#00d4aa', '&:hover': { borderColor: '#00d4aa', bgcolor: 'rgba(0,212,170,0.1)' } }}
                  >
                    M'assigner au projet
                  </Button>
                </Box>
              )}

              {/* Assigned users list */}
              <Box sx={{ maxHeight: 250, overflowY: 'auto' }}>
                {assignments.map((a) => {
                  const roleLabel = {
                    SUPERADMIN: 'Super Admin',
                    ADMIN: 'Admin',
                    DEV: 'Développeur',
                    TECHNICIEN: 'Technicien'
                  }[a.user.role] || a.user.role;

                  return (
                    <Box 
                      key={a.id}
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        p: 1.5, 
                        px: 2,
                        borderBottom: '1px solid rgba(255,255,255,0.05)',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' }
                      }}
                    >
                      <Avatar sx={{ width: 32, height: 32, bgcolor: '#00d4aa20', color: '#00d4aa', fontSize: '0.8rem', mr: 1.5 }}>
                        {a.user.firstName?.[0] || a.user.email[0].toUpperCase()}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {a.user.firstName || ''} {a.user.lastName || a.user.email}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {roleLabel}
                        </Typography>
                      </Box>
                      {isAdmin && (
                        <Tooltip title="Retirer du projet">
                          <IconButton 
                            size="small" 
                            onClick={() => handleUnassignUser(a.userId)}
                            sx={{ color: 'error.main' }}
                          >
                            <UserMinus size={16} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  );
                })}

                {assignments.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
                    Aucun membre assigné à ce projet.
                  </Typography>
                )}
              </Box>

              {/* Available users to add */}
              {isAdmin && availableUsers.length > 0 && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ px: 2, display: 'block', mb: 1 }}>
                    Utilisateurs disponibles
                  </Typography>
                  <Box sx={{ maxHeight: 150, overflowY: 'auto' }}>
                    {availableUsers.map((u) => {
                      const roleLabel = {
                        SUPERADMIN: 'Super Admin',
                        ADMIN: 'Admin',
                        DEV: 'Développeur',
                        TECHNICIEN: 'Technicien'
                      }[u.role] || u.role;

                      return (
                        <Box 
                          key={u.id}
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            p: 1, 
                            px: 2,
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'rgba(0,212,170,0.1)' }
                          }}
                          onClick={() => handleAssignUser(u.id)}
                        >
                          <Avatar sx={{ width: 28, height: 28, bgcolor: 'rgba(255,255,255,0.1)', fontSize: '0.75rem', mr: 1.5 }}>
                            {u.firstName?.[0] || u.email[0].toUpperCase()}
                          </Avatar>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                              {u.firstName || ''} {u.lastName || u.email}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                              {roleLabel}
                            </Typography>
                          </Box>
                          <UserPlus size={14} color="#00d4aa" />
                        </Box>
                      );
                    })}
                  </Box>
                </>
              )}
            </Box>
          )}

          {/* Tab 1: Notifications */}
          {teamDialogTab === 1 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ p: 2, pb: 1 }}>
                {isDev 
                  ? 'Configurez vos propres notifications pour ce projet.'
                  : 'Configurez les notifications par utilisateur et par type d\'événement.'
                }
              </Typography>
              
              {/* Users list with expandable preferences - DEV only sees themselves */}
              <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                {notificationUsers
                  .filter(u => isDev ? u.id === user?.id : true)
                  .map((u) => {
                  const isCreator = u.id === notifCreatedById;
                  const isExpanded = expandedUsers[u.id];
                  const roleLabel = {
                    SUPERADMIN: 'Super Admin',
                    ADMIN: 'Admin',
                    DEV: 'Développeur',
                    TECHNICIEN: 'Technicien',
                    CLIENTADMIN: 'Admin Client',
                    CLIENT: 'Client'
                  }[u.role] || u.role;
                  const isInternal = ['ADMIN', 'SUPERADMIN', 'DEV', 'TECHNICIEN'].includes(u.role);
                  const canEditThisUser = isAdmin || u.id === user?.id; // DEV can only edit own prefs

                  return (
                    <Box 
                      key={u.id}
                      sx={{ 
                        borderBottom: '1px solid rgba(255,255,255,0.05)',
                        '&:last-child': { borderBottom: 'none' }
                      }}
                    >
                      {/* User row */}
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          p: 1.5, 
                          px: 2,
                          cursor: u.isSubscribed ? 'pointer' : 'default',
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' }
                        }}
                        onClick={() => u.isSubscribed && toggleUserExpand(u.id)}
                      >
                        {/* Expand/Collapse icon */}
                        <Box sx={{ width: 24, mr: 1 }}>
                          {u.isSubscribed && (
                            isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />
                          )}
                        </Box>

                        {/* User info */}
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {u.firstName} {u.lastName}
                            </Typography>
                            {isCreator && (
                              <Chip label="Créateur" size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#00d4aa20', color: '#00d4aa' }} />
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {u.email} • {roleLabel}
                            {!isInternal && u.company?.name && ` • ${u.company.name}`}
                          </Typography>
                        </Box>

                        {/* Subscribe toggle - Admin can toggle anyone, DEV can toggle themselves */}
                        {canEditThisUser && (
                          <Switch
                            checked={u.isSubscribed}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleToggleUserSubscription(u.id, u.isSubscribed);
                            }}
                            size="small"
                            color="primary"
                          />
                        )}
                      </Box>

                      {/* Expanded preferences */}
                      <Collapse in={isExpanded && u.isSubscribed}>
                        <Box sx={{ pl: 6, pr: 2, pb: 2, bgcolor: 'rgba(0,0,0,0.1)' }}>
                          {/* Quick toggle all */}
                          {canEditThisUser && (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                              <Button 
                                size="small" 
                                onClick={() => handleToggleAllPrefs(u.id, true)}
                                sx={{ fontSize: '0.7rem', mr: 1 }}
                              >
                                Tout activer
                              </Button>
                              <Button 
                                size="small" 
                                onClick={() => handleToggleAllPrefs(u.id, false)}
                                sx={{ fontSize: '0.7rem' }}
                              >
                                Tout désactiver
                              </Button>
                            </Box>
                          )}

                          {/* Individual preferences */}
                          <Grid container spacing={1}>
                            {[
                              { key: 'notifyMessages', icon: MessageSquare, label: 'Messages', desc: 'Nouveau message dans le chat' },
                              { key: 'notifyDocuments', icon: FileCheck, label: 'Documents', desc: 'Ajout/modification de document' },
                              { key: 'notifyStatus', icon: RefreshCw, label: 'Statut', desc: 'Changement de statut workflow' },
                              { key: 'notifyOptions', icon: ToggleLeft, label: 'Options', desc: 'Acceptation/refus des options' }
                            ].map(({ key, icon: Icon, label, desc }) => (
                              <Grid item xs={6} key={key}>
                                <Paper
                                  sx={{
                                    p: 1.5,
                                    bgcolor: u.prefs?.[key] ? 'rgba(0,212,170,0.1)' : 'rgba(255,255,255,0.02)',
                                    border: `1px solid ${u.prefs?.[key] ? 'rgba(0,212,170,0.3)' : 'rgba(255,255,255,0.05)'}`,
                                    cursor: canEditThisUser ? 'pointer' : 'default',
                                    transition: 'all 0.2s',
                                    '&:hover': canEditThisUser ? { borderColor: 'rgba(0,212,170,0.5)' } : {}
                                  }}
                                  onClick={() => canEditThisUser && handleUpdateNotifPref(u.id, key, !u.prefs?.[key])}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <Icon size={14} color={u.prefs?.[key] ? '#00d4aa' : '#888'} />
                                    <Typography variant="caption" sx={{ fontWeight: 600, color: u.prefs?.[key] ? '#00d4aa' : 'text.secondary' }}>
                                      {label}
                                    </Typography>
                                    {canEditThisUser && (
                                      <Checkbox
                                        checked={u.prefs?.[key] || false}
                                        size="small"
                                        sx={{ ml: 'auto', p: 0 }}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={() => handleUpdateNotifPref(u.id, key, !u.prefs?.[key])}
                                      />
                                    )}
                                  </Box>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', display: 'block' }}>
                                    {desc}
                                  </Typography>
                                </Paper>
                              </Grid>
                            ))}
                          </Grid>
                        </Box>
                      </Collapse>
                    </Box>
                  );
                })}

                {(isDev ? !notificationUsers.find(u => u.id === user?.id) : notificationUsers.length === 0) && (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
                    {isDev 
                      ? 'Vous n\'êtes pas encore inscrit aux notifications. Assignez-vous au projet d\'abord.'
                      : 'Aucun utilisateur disponible pour les notifications.'
                    }
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <Button onClick={() => setTeamDialogOpen(false)} variant="contained" fullWidth>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Client Notifications Dialog - Simplified */}
      <Dialog 
        open={clientNotifDialogOpen} 
        onClose={() => setClientNotifDialogOpen(false)} 
        maxWidth="xs" 
        fullWidth
        PaperProps={{ sx: { bgcolor: 'background.paper', backgroundImage: 'none' } }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <Bell size={20} color="#00d4aa" />
          Mes notifications
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Configurez les notifications que vous souhaitez recevoir pour ce devis.
          </Typography>
          
          {/* Master toggle */}
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              p: 2,
              mb: 2,
              bgcolor: myNotifPrefs?.isSubscribed ? 'rgba(0,212,170,0.1)' : 'rgba(255,255,255,0.02)',
              borderRadius: 2,
              border: `1px solid ${myNotifPrefs?.isSubscribed ? 'rgba(0,212,170,0.3)' : 'rgba(255,255,255,0.1)'}`
            }}
          >
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Recevoir les notifications
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {myNotifPrefs?.isSubscribed ? 'Les notifications sont activées' : 'Les notifications sont désactivées'}
              </Typography>
            </Box>
            <Switch
              checked={myNotifPrefs?.isSubscribed || false}
              onChange={handleToggleMySubscription}
              color="primary"
            />
          </Box>

          {/* Individual preferences */}
          {myNotifPrefs?.isSubscribed && (
            <Grid container spacing={1.5}>
              {[
                { key: 'notifyMessages', icon: MessageSquare, label: 'Messages', desc: 'Nouveau message dans le chat' },
                { key: 'notifyDocuments', icon: FileCheck, label: 'Documents', desc: 'Ajout/modification de document' },
                { key: 'notifyStatus', icon: RefreshCw, label: 'Statut', desc: 'Changement de statut du devis' },
                { key: 'notifyOptions', icon: ToggleLeft, label: 'Options', desc: 'Acceptation/refus des options' }
              ].map(({ key, icon: Icon, label, desc }) => (
                <Grid item xs={6} key={key}>
                  <Paper
                    sx={{
                      p: 1.5,
                      bgcolor: myNotifPrefs?.[key] ? 'rgba(0,212,170,0.1)' : 'rgba(255,255,255,0.02)',
                      border: `1px solid ${myNotifPrefs?.[key] ? 'rgba(0,212,170,0.3)' : 'rgba(255,255,255,0.05)'}`,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': { borderColor: 'rgba(0,212,170,0.5)' }
                    }}
                    onClick={() => handleUpdateMyNotifPref(key, !myNotifPrefs?.[key])}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Icon size={14} color={myNotifPrefs?.[key] ? '#00d4aa' : '#888'} />
                      <Typography variant="caption" sx={{ fontWeight: 600, color: myNotifPrefs?.[key] ? '#00d4aa' : 'text.secondary' }}>
                        {label}
                      </Typography>
                      <Checkbox
                        checked={myNotifPrefs?.[key] || false}
                        size="small"
                        sx={{ ml: 'auto', p: 0 }}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => handleUpdateMyNotifPref(key, !myNotifPrefs?.[key])}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', display: 'block' }}>
                      {desc}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <Button onClick={() => setClientNotifDialogOpen(false)} variant="contained" fullWidth>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Template Selector Dialog */}
      <TemplateSelector
        open={templateSelectorOpen}
        onClose={() => setTemplateSelectorOpen(false)}
        onApply={handleTemplateApplied}
        submissionId={id}
        projectTypeId={submission?.projectType?.id}
      />

      {/* Convert to Template Dialog */}
      <Dialog 
        open={convertDialogOpen} 
        onClose={() => setConvertDialogOpen(false)}
        PaperProps={{ sx: { bgcolor: 'background.paper', backgroundImage: 'none', minWidth: 400 } }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Copy size={20} color="#00d4aa" />
          Convertir en template
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Créez un template réutilisable à partir de la structure actuelle du devis.
          </Typography>
          <TextField
            fullWidth
            label="Nom du template"
            value={convertTemplateName}
            onChange={(e) => setConvertTemplateName(e.target.value)}
            placeholder="Ex: Site vitrine standard"
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setConvertDialogOpen(false)} color="inherit">Annuler</Button>
          <Button 
            onClick={handleConvertToTemplate} 
            variant="contained"
            disabled={!convertTemplateName.trim()}
          >
            Créer le template
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Gallery */}
      <PreviewGallery
        open={previewGalleryOpen}
        onClose={() => setPreviewGalleryOpen(false)}
        submissionId={id}
      />

      {/* Assign Client Dialog */}
      <Dialog
        open={assignClientDialogOpen}
        onClose={() => setAssignClientDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: 'background.paper', backgroundImage: 'none' } }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <Building2 size={20} color="#3b82f6" />
          Attribuer un client
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Sélectionnez l'entreprise et le contact client pour ce devis.
          </Typography>

          <Stack spacing={3}>
            {/* Sélection entreprise */}
            <Autocomplete
              options={clientCompanies}
              getOptionLabel={(option) => option.name || ''}
              value={clientCompanies.find(c => c.id === assignClientForm.companyId) || null}
              onChange={(_, newValue) => {
                setAssignClientForm({ companyId: newValue?.id || '', clientId: '' });
                fetchCompanyUsers(newValue?.id);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Entreprise"
                  placeholder="Rechercher une entreprise..."
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <Search size={18} style={{ marginLeft: 8, marginRight: 4, opacity: 0.5 }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Building2 size={16} color="#00d4aa" />
                    <Typography variant="body1">{option.name}</Typography>
                  </Box>
                </li>
              )}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              noOptionsText="Aucune entreprise trouvée"
            />

            {/* Sélection contact */}
            {assignClientForm.companyId && clientCompanyUsers.length > 0 && (
              <FormControl fullWidth>
                <InputLabel id="assign-client-select-label">Contact principal</InputLabel>
                <Select
                  labelId="assign-client-select-label"
                  value={assignClientForm.clientId}
                  label="Contact principal"
                  onChange={(e) => setAssignClientForm(prev => ({ ...prev, clientId: e.target.value }))}
                >
                  <MenuItem value="">
                    <em>Aucun contact spécifique</em>
                  </MenuItem>
                  {clientCompanyUsers.map((u) => (
                    <MenuItem key={u.id} value={u.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <User size={16} />
                        {u.firstName} {u.lastName} {u.role === 'CLIENTADMIN' ? '(Admin)' : ''}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {assignClientForm.companyId && clientCompanyUsers.length === 0 && (
              <Alert severity="warning" sx={{ bgcolor: 'rgba(245, 158, 11, 0.1)' }}>
                Cette entreprise n'a pas encore d'utilisateurs. Le devis sera attribué à l'entreprise uniquement.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <Button onClick={() => setAssignClientDialogOpen(false)} color="inherit">
            Annuler
          </Button>
          <Button 
            onClick={handleAssignClient}
            variant="contained"
            disabled={!assignClientForm.companyId || assignClientLoading}
            sx={{ bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }}
          >
            {assignClientLoading ? 'Attribution...' : 'Attribuer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: 'background.paper', backgroundImage: 'none' } }}
      >
        <DialogTitle sx={{ color: 'error.main', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Trash2 size={24} />
          Supprimer le devis
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Êtes-vous sûr de vouloir supprimer le devis <strong>"{submission?.title}"</strong> ?
          </Typography>
          <Alert severity="warning" sx={{ bgcolor: 'rgba(245, 158, 11, 0.1)' }}>
            Cette action est irréversible. Toutes les données associées (sections, items, documents, messages, etc.) seront supprimées définitivement.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <Button onClick={() => setDeleteDialogOpen(false)} color="inherit" disabled={deleteLoading}>
            Annuler
          </Button>
          <Button 
            onClick={handleDeleteSubmission}
            variant="contained"
            color="error"
            disabled={deleteLoading}
          >
            {deleteLoading ? 'Suppression...' : 'Supprimer définitivement'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SubmissionDetail;
